import React, { useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import { Download, Upload, Shield, Database, FileJson, FileSpreadsheet, AlertTriangle, CheckCircle, FileArchive, Settings } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminBackupPage = () => {
  const { getAuthHeaders } = useApp();
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [downloadingConfig, setDownloadingConfig] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);

  const handleExportJSON = async () => {
    // Check if logged in first
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast.error('Nu ești autentificat. Te rog să te loghezi din nou.');
      return;
    }
    
    setLoading(true);
    try {
      console.log('Starting backup export...');
      
      const { data } = await axios.get(`${BACKEND_URL}/api/backup/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 60000 // 60 seconds timeout for large backups
      });
      
      console.log('Backup data received, apartments:', data?.apartments?.length || 0);
      
      // Download as JSON file
      const jsonStr = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rentiasi_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setLastBackup(new Date().toISOString());
      toast.success('Backup descărcat cu succes!');
    } catch (error) {
      console.error('Backup error:', error);
      
      let errorMsg = 'Eroare necunoscută';
      if (error.response?.status === 401 || error.response?.status === 403) {
        errorMsg = 'Sesiune expirată. Te rog să te deloghezi și să te loghezi din nou.';
        // Clear invalid token
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(`Eroare la export: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExportTimeEntriesCSV = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/backup/time-entries-csv`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `time_entries_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Time entries exported!');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handleExportInvoicesCSV = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/backup/invoices-csv`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Invoices exported!');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handleRestore = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!window.confirm('Ești sigur că vrei să restaurezi din backup? Datele existente vor fi păstrate, doar cele noi vor fi adăugate.')) {
      return;
    }

    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append('backup_file', file);

      const { data } = await axios.post(`${BACKEND_URL}/api/backup/restore`, formData, {
        headers: getAuthHeaders()
      });

      // Build restore summary
      const r = data.restored || {};
      const parts = [];
      if (r.apartments) parts.push(`${r.apartments} apartamente`);
      if (r.time_entries) parts.push(`${r.time_entries} pontări`);
      if (r.invoices) parts.push(`${r.invoices} facturi`);
      if (r.clients) parts.push(`${r.clients} clienți facturare`);
      if (r.contracts) parts.push(`${r.contracts} contracte`);
      if (r.service_sheets) parts.push(`${r.service_sheets} fișe service`);
      if (r.service_clients) parts.push(`${r.service_clients} clienți service`);
      if (r.service_providers) parts.push(`${r.service_providers} provideri service`);
      if (r.newsletter_subscribers) parts.push(`${r.newsletter_subscribers} abonați`);
      
      const summary = parts.length > 0 ? parts.join(', ') : 'Date actualizate';
      toast.success(`Restaurat cu succes: ${summary}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore');
    } finally {
      setRestoring(false);
      event.target.value = '';
    }
  };

  const handleDownloadConfigZip = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast.error('Nu ești autentificat. Te rog să te loghezi din nou.');
      return;
    }

    setDownloadingConfig(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/backup/project-zip`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob',
        timeout: 120000 // 2 minutes timeout for large file
      });

      // Create download link
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rentiasi_project_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Arhiva ZIP completă a fost descărcată!');
    } catch (error) {
      console.error('Project ZIP download error:', error);
      let errorMsg = 'Eroare necunoscută';
      if (error.response?.status === 401 || error.response?.status === 403) {
        errorMsg = 'Sesiune expirată. Te rog să te deloghezi și să te loghezi din nou.';
      } else if (error.message) {
        errorMsg = error.message;
      }
      toast.error(`Eroare la descărcare: ${errorMsg}`);
    } finally {
      setDownloadingConfig(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-serif flex items-center gap-3">
            <Shield className="text-primary" />
            Backup & Protecție Date
          </h1>
          <p className="text-muted-foreground mt-2">
            Descarcă backup-ul regulat pentru a-ți proteja datele
          </p>
        </div>

        {/* Warning */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex gap-4">
          <AlertTriangle className="text-yellow-500 flex-shrink-0" size={24} />
          <div>
            <p className="font-semibold text-yellow-500">Important!</p>
            <p className="text-sm text-yellow-400/80">
              Datele sunt stocate pe server și pot fi pierdute în caz de resetare. 
              Recomandăm să descarci backup-ul cel puțin o dată pe săptămână.
            </p>
          </div>
        </div>

        {/* Config ZIP Download - SEPARATE SECTION */}
        <div className="glass p-6 border-2 border-cyan-500/30 bg-cyan-500/5">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-cyan-500/20 rounded-xl flex-shrink-0">
              <Settings className="text-cyan-400" size={32} />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold flex items-center gap-2">
                📦 Proiect Complet (ZIP)
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">Ready to Deploy</span>
              </h3>
              <p className="text-muted-foreground text-sm mt-1">
                Descarcă arhiva completă cu tot proiectul pregătit pentru instalare pe VPS:
              </p>
              <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                <div className="bg-slate-800/50 p-2 rounded">
                  <p className="font-medium text-cyan-400 mb-1">📁 Backend</p>
                  <ul className="text-muted-foreground space-y-0.5">
                    <li>✓ server.py (complet)</li>
                    <li>✓ requirements.txt</li>
                    <li>✓ .env.example</li>
                  </ul>
                </div>
                <div className="bg-slate-800/50 p-2 rounded">
                  <p className="font-medium text-green-400 mb-1">🌐 Frontend</p>
                  <ul className="text-muted-foreground space-y-0.5">
                    <li>✓ <strong className="text-white">build/</strong> (gata de servit)</li>
                    <li>✓ .env.example</li>
                    <li className="text-green-400">Nu necesită yarn build!</li>
                  </ul>
                </div>
                <div className="bg-slate-800/50 p-2 rounded">
                  <p className="font-medium text-yellow-400 mb-1">⚙️ Configurare</p>
                  <ul className="text-muted-foreground space-y-0.5">
                    <li>✓ nginx.conf</li>
                    <li>✓ systemd service</li>
                  </ul>
                </div>
                <div className="bg-slate-800/50 p-2 rounded">
                  <p className="font-medium text-purple-400 mb-1">🚀 Instalare</p>
                  <ul className="text-muted-foreground space-y-0.5">
                    <li>✓ install.sh (automat)</li>
                    <li>✓ README.md (pași)</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4">
                <Button 
                  onClick={handleDownloadConfigZip} 
                  disabled={downloadingConfig} 
                  className="bg-gradient-to-r from-cyan-600 to-green-600 hover:from-cyan-700 hover:to-green-700"
                  size="lg"
                >
                  <FileArchive size={20} className="mr-2" />
                  {downloadingConfig ? 'Se generează arhiva...' : 'Descarcă Proiect Complet (ZIP)'}
                </Button>
              </div>
              <p className="text-xs text-green-400 mt-3">
                ✅ Arhiva include frontend-ul pre-built - instalare directă fără reconstruire!
              </p>
            </div>
          </div>
        </div>

        {/* Backup Complet */}
        <div className="glass p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/20 rounded-xl">
              <Database className="text-primary" size={32} />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold">Backup Complet (JSON) <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded ml-2">v2.2</span></h3>
              <p className="text-muted-foreground text-sm mt-1">
                Include TOATE datele site-ului (export complet):
              </p>
              <ul className="text-xs text-muted-foreground mt-2 grid grid-cols-3 gap-1">
                <li>✓ Apartamente</li>
                <li>✓ Categorii</li>
                <li>✓ Imagini (base64)</li>
                <li>✓ Pontări</li>
                <li>✓ Facturi</li>
                <li>✓ Clienți facturare</li>
                <li>✓ Colaboratori</li>
                <li>✓ Contracte</li>
                <li>✓ Chiriași/Proprietari</li>
                <li>✓ Abonați newsletter</li>
                <li>✓ Mesaje contact</li>
                <li>✓ Toate setările</li>
                <li className="text-orange-400 font-medium">✓ Fișe Service (toate)</li>
                <li className="text-orange-400 font-medium">✓ Clienți Service</li>
                <li className="text-orange-400 font-medium">✓ Provideri (UK/IT/RO)</li>
              </ul>
              <div className="mt-4 flex gap-3">
                <Button onClick={handleExportJSON} disabled={loading} className="bg-primary">
                  <Download size={18} className="mr-2" />
                  {loading ? 'Se descarcă...' : 'Descarcă Backup Complet'}
                </Button>
              </div>
              {lastBackup && (
                <p className="text-xs text-green-400 mt-3 flex items-center gap-1">
                  <CheckCircle size={14} />
                  Ultimul backup: {new Date(lastBackup).toLocaleString('ro-RO')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Export CSV */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="glass p-6">
            <div className="flex items-center gap-3 mb-4">
              <FileSpreadsheet className="text-green-400" size={24} />
              <h3 className="font-semibold">Export Pontări (CSV)</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Pentru Excel sau Google Sheets
            </p>
            <Button onClick={handleExportTimeEntriesCSV} variant="outline" className="w-full">
              <Download size={16} className="mr-2" />
              Descarcă CSV
            </Button>
          </div>

          <div className="glass p-6">
            <div className="flex items-center gap-3 mb-4">
              <FileSpreadsheet className="text-blue-400" size={24} />
              <h3 className="font-semibold">Export Facturi (CSV)</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Pentru Excel sau Google Sheets
            </p>
            <Button onClick={handleExportInvoicesCSV} variant="outline" className="w-full">
              <Download size={16} className="mr-2" />
              Descarcă CSV
            </Button>
          </div>
        </div>

        {/* Restore */}
        <div className="glass p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-orange-500/20 rounded-xl">
              <Upload className="text-orange-400" size={32} />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold">Restaurează din Backup</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Încarcă un fișier JSON salvat anterior pentru a restaura datele.
                <span className="block mt-1 text-xs text-green-400">
                  ✓ Restaurează toate datele inclusiv: apartamente, facturi, contracte, fișe service, clienți, setări
                </span>
              </p>
              <div className="mt-4">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleRestore}
                    className="hidden"
                    disabled={restoring}
                  />
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-orange-400/50 text-orange-400 hover:bg-orange-400/10 transition-colors ${restoring ? 'opacity-50' : ''}`}>
                    <Upload size={18} />
                    {restoring ? 'Se restaurează...' : 'Selectează fișier JSON'}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="glass p-6 bg-slate-800/30">
          <h3 className="font-semibold mb-3">💡 Sfaturi pentru protecția datelor:</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• Descarcă backup-ul JSON cel puțin o dată pe săptămână</li>
            <li>• Salvează fișierele într-un loc sigur (Google Drive, Dropbox, etc.)</li>
            <li>• Păstrează mai multe versiuni de backup</li>
            <li>• După ce site-ul va fi pe domeniul stabil (rentiasi.ro), datele vor fi mai sigure</li>
          </ul>
        </div>

        {/* Ce se restaurează */}
        <div className="glass p-6">
          <h3 className="font-semibold mb-3 text-green-400">✓ Ce se restaurează din backup:</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div className="bg-slate-800/50 p-3 rounded">
              <p className="font-medium text-white mb-1">📦 Conținut</p>
              <ul className="text-muted-foreground space-y-0.5">
                <li>• Apartamente</li>
                <li>• Categorii</li>
                <li>• Imagini</li>
              </ul>
            </div>
            <div className="bg-slate-800/50 p-3 rounded">
              <p className="font-medium text-white mb-1">💼 Facturare</p>
              <ul className="text-muted-foreground space-y-0.5">
                <li>• Pontări</li>
                <li>• Facturi</li>
                <li>• Clienți</li>
              </ul>
            </div>
            <div className="bg-slate-800/50 p-3 rounded">
              <p className="font-medium text-white mb-1">📋 Contracte</p>
              <ul className="text-muted-foreground space-y-0.5">
                <li>• Contracte</li>
                <li>• Chiriași</li>
                <li>• Proprietari</li>
              </ul>
            </div>
            <div className="bg-slate-800/50 p-3 rounded border border-orange-500/30">
              <p className="font-medium text-orange-400 mb-1">🔧 Service</p>
              <ul className="text-muted-foreground space-y-0.5">
                <li>• Fișe Service</li>
                <li>• Clienți Service</li>
                <li>• Provideri</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Cerință Backup Complet ZIP */}
        <div className="glass p-6 border-2 border-purple-500/30 bg-purple-500/5 select-text">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-500/20 rounded-xl flex-shrink-0">
              <FileArchive className="text-purple-400" size={32} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-semibold flex items-center gap-2">
                  📦 Cerințe Finale pentru Deployment
                </h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const text = `Am nevoie să îmi furnizați un singur fișier de backup complet (arhivă ZIP) care să conțină întregul site în starea finală și funcțională — frontend, backend, configurări, structura de directoare, fișierele de build și toate dependențele necesare — astfel încât instalarea să fie posibilă direct prin extragerea arhivei pe server, fără să mai fie nevoie să iau fișiere din gist, să reconstruiesc manual sau să rezolv conflicte de pachete.

Am nevoie ca proiectul să fie livrat într-o versiune stabilă și modernă care să includă un frontend construit exclusiv cu Vite sau Next.js (fără CRACO, fără react-scripts și fără pachete depășite precum ajv-keywords sau workbox), un fișier .env.example complet atât pentru backend cât și pentru frontend, un script automat de instalare care să configureze Node 18, să instaleze dependințele, să facă build-ul frontend-ului și să pregătească backend-ul, o structură clară de directoare cu backend, frontend și un nginx.conf.example, documentație de instalare în 10 pași simpli, backend complet decuplat de frontend astfel încât să servească doar API-ul, și documentație completă a endpoint-urilor API, pentru ca instalarea și rularea proiectului să fie predictibile, rapide și fără conflicte.`;
                    navigator.clipboard.writeText(text);
                    toast.success('Ambele cerințe copiate în clipboard!');
                  }}
                  className="text-xs bg-purple-600 hover:bg-purple-700 text-white border-0"
                >
                  <Download size={14} className="mr-1" />
                  Copiază TOATE cerințele
                </Button>
              </div>
              
              {/* Cerința 1: Backup ZIP */}
              <div className="p-3 bg-slate-900/50 rounded border border-purple-500/20 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-purple-400">📦 Cerința 1: Arhivă ZIP Completă</p>
                  <button
                    onClick={() => {
                      const text = `Am nevoie să îmi furnizați un singur fișier de backup complet (arhivă ZIP) care să conțină întregul site în starea finală și funcțională — frontend, backend, configurări, structura de directoare, fișierele de build și toate dependențele necesare — astfel încât instalarea să fie posibilă direct prin extragerea arhivei pe server, fără să mai fie nevoie să iau fișiere din gist, să reconstruiesc manual sau să rezolv conflicte de pachete.`;
                      navigator.clipboard.writeText(text);
                      toast.success('Cerința 1 copiată!');
                    }}
                    className="text-xs text-purple-400 hover:text-white"
                  >
                    [Copiază]
                  </button>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed cursor-text" style={{ userSelect: 'text' }}>
                  Am nevoie să îmi furnizați un <strong className="text-white">singur fișier de backup complet (arhivă ZIP)</strong> care să conțină 
                  întregul site în starea finală și funcțională — <strong className="text-white">frontend</strong>, <strong className="text-white">backend</strong>, 
                  <strong className="text-white"> configurări</strong>, <strong className="text-white">structura de directoare</strong>, 
                  <strong className="text-white"> fișierele de build</strong> și <strong className="text-white">toate dependențele necesare</strong> — 
                  astfel încât instalarea să fie posibilă direct prin extragerea arhivei pe server, 
                  <span className="text-red-400"> fără</span> să mai fie nevoie să iau fișiere din gist, 
                  <span className="text-red-400"> fără</span> să reconstruiesc manual sau 
                  <span className="text-red-400"> fără</span> să rezolv conflicte de pachete.
                </p>
              </div>

              {/* Cerința 2: Versiune Stabilă */}
              <div className="p-3 bg-slate-900/50 rounded border border-blue-500/20 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-blue-400">⚡ Cerința 2: Versiune Stabilă și Modernă</p>
                  <button
                    onClick={() => {
                      const text = `Am nevoie ca proiectul să fie livrat într-o versiune stabilă și modernă care să includă un frontend construit exclusiv cu Vite sau Next.js (fără CRACO, fără react-scripts și fără pachete depășite precum ajv-keywords sau workbox), un fișier .env.example complet atât pentru backend cât și pentru frontend, un script automat de instalare care să configureze Node 18, să instaleze dependințele, să facă build-ul frontend-ului și să pregătească backend-ul, o structură clară de directoare cu backend, frontend și un nginx.conf.example, documentație de instalare în 10 pași simpli, backend complet decuplat de frontend astfel încât să servească doar API-ul, și documentație completă a endpoint-urilor API, pentru ca instalarea și rularea proiectului să fie predictibile, rapide și fără conflicte.`;
                      navigator.clipboard.writeText(text);
                      toast.success('Cerința 2 copiată!');
                    }}
                    className="text-xs text-blue-400 hover:text-white"
                  >
                    [Copiază]
                  </button>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed cursor-text" style={{ userSelect: 'text' }}>
                  Am nevoie ca proiectul să fie livrat într-o <strong className="text-white">versiune stabilă și modernă</strong> care să includă 
                  un frontend construit exclusiv cu <strong className="text-white">Vite</strong> sau <strong className="text-white">Next.js</strong> 
                  (<span className="text-red-400">fără CRACO</span>, <span className="text-red-400">fără react-scripts</span> și 
                  <span className="text-red-400"> fără pachete depășite</span> precum <code className="bg-slate-700 px-1 rounded text-xs">ajv-keywords</code> sau 
                  <code className="bg-slate-700 px-1 rounded text-xs"> workbox</code>), 
                  un fișier <strong className="text-white">.env.example</strong> complet atât pentru backend cât și pentru frontend, 
                  un <strong className="text-white">script automat de instalare</strong> care să configureze Node 18, să instaleze dependințele, 
                  să facă build-ul frontend-ului și să pregătească backend-ul, 
                  o <strong className="text-white">structură clară de directoare</strong> cu backend, frontend și un nginx.conf.example, 
                  <strong className="text-white"> documentație de instalare în 10 pași simpli</strong>, 
                  backend <strong className="text-white">complet decuplat</strong> de frontend astfel încât să servească doar API-ul, 
                  și <strong className="text-white">documentație completă a endpoint-urilor API</strong>, 
                  pentru ca instalarea și rularea proiectului să fie <strong className="text-green-400">predictibile, rapide și fără conflicte</strong>.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-800/50 p-3 rounded">
                  <p className="font-medium text-purple-400 mb-2">📁 Conținut arhivă ZIP:</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>✓ <strong className="text-white">Frontend complet</strong> (cod sursă + build)</li>
                    <li>✓ <strong className="text-white">Backend complet</strong> (server.py + requirements.txt)</li>
                    <li>✓ <strong className="text-white">Fișiere .env.example</strong> (frontend + backend)</li>
                    <li>✓ <strong className="text-white">nginx.conf.example</strong></li>
                    <li>✓ <strong className="text-white">Script de instalare</strong> (install.sh)</li>
                    <li>✓ <strong className="text-white">Structura completă</strong> de directoare</li>
                    <li>✓ <strong className="text-white">Documentație</strong> (README.md)</li>
                  </ul>
                </div>
                <div className="bg-slate-800/50 p-3 rounded">
                  <p className="font-medium text-green-400 mb-2">✅ Beneficii:</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• Instalare directă prin extragere pe server</li>
                    <li>• <span className="text-red-400">Fără</span> fișiere din gist separate</li>
                    <li>• <span className="text-red-400">Fără</span> reconstruire manuală</li>
                    <li>• <span className="text-red-400">Fără</span> conflicte de pachete</li>
                    <li>• <span className="text-red-400">Fără</span> dependințe lipsă</li>
                    <li>• Deployment rapid și predictibil</li>
                  </ul>
                </div>
              </div>

              <div className="mt-4 p-3 bg-slate-800/50 rounded">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-yellow-400">📋 Structură așteptată în ZIP:</p>
                  <button
                    onClick={() => {
                      const structure = `rentiasi-complete.zip
├── backend/
│   ├── server.py
│   ├── requirements.txt
│   └── .env.example
├── frontend/
│   ├── src/
│   ├── dist/                 # Build-ul gata
│   ├── package.json
│   └── .env.example
├── nginx.conf.example
├── install.sh                # Script automat
├── systemd/
│   └── rentiasi-backend.service
└── README.md                 # Pași instalare`;
                      navigator.clipboard.writeText(structure);
                      toast.success('Structură copiată!');
                    }}
                    className="text-xs text-gray-400 hover:text-white"
                  >
                    [Copiază]
                  </button>
                </div>
                <pre className="text-xs text-gray-400 select-text" style={{ userSelect: 'text' }}>{`rentiasi-complete.zip
├── backend/
│   ├── server.py
│   ├── requirements.txt
│   └── .env.example
├── frontend/
│   ├── src/
│   ├── dist/                 # Build-ul gata
│   ├── package.json
│   └── .env.example
├── nginx.conf.example
├── install.sh                # Script automat
├── systemd/
│   └── rentiasi-backend.service
└── README.md                 # Pași instalare`}</pre>
              </div>

              <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded">
                <p className="text-purple-300 text-sm select-text" style={{ userSelect: 'text' }}>
                  <strong>💡 Notă:</strong> Acest tip de backup (arhivă ZIP cu tot proiectul) trebuie solicitat 
                  separat și reprezintă pachetul final de deployment pentru VPS.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminBackupPage;
